import React, { useEffect, useState } from 'react';
import { Table, Button, Upload, Row, notification } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getCertificate, generateCertificate, uploadFinalCertificate } from '@app/api/prImportFreeSales.api';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';

interface Certificate {
  key: string;
  pr_code: string;
  generatedCertificateUrl?: string;
  certified_certificate?: string;
  completedCertificateUrl?: string;
}

interface CertificateListProps {
  id: string | null;
  pr_code: string | null;
  PRstatus?: string | null;
}

const CertificateList: React.FC<CertificateListProps> = ({ id, pr_code, PRstatus }) => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = user?.usertype || '';

  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    if (id) {
      getCertificate(Number(id))
        .then((data) => {
          console.log('Certificate API Response:', data);

          const certificateData = data?.pr_certificates?.[0];

          setCertificate({
            key: id,
            pr_code: pr_code || '',
            certified_certificate: certificateData?.certified_certificate || null,
            generatedCertificateUrl: certificateData?.generated_certificate_url || undefined,
            completedCertificateUrl:
              certificateData?.certified_certificate_url &&
              certificateData?.certified_certificate_url !==
                'https://vraasback.ksoft.web.lk//storage/product_registration/certificates/certified/'
                ? certificateData.certified_certificate_url
                : undefined,
          });
        })
        .catch((err) => {
          console.error('Failed to fetch certificate:', err);
          setCertificate({
            key: id,
            pr_code: pr_code || '',
            generatedCertificateUrl: undefined,
            completedCertificateUrl: undefined,
          });
        });
    }
  }, [id, pr_code]);

  const showCertificate = (pdfUrl: string) => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    } else {
      notification.error({ message: 'Error', description: 'Invalid PDF URL' });
    }
  };

  const handleGenerateCertificate = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await generateCertificate(Number(id));
      console.log('Generate Certificate Response:', response);

      await getCertificate(Number(id))
        .then((data) => {
          const certificateData = data?.pr_certificates?.[0];
          setCertificate({
            key: id,
            pr_code: pr_code || '',
            certified_certificate: certificateData?.certified_certificate || null,
            generatedCertificateUrl: certificateData?.generated_certificate_url || undefined,
            completedCertificateUrl:
              certificateData?.certified_certificate_url &&
              certificateData?.certified_certificate_url !==
                'https://vraasback.ksoft.web.lk//storage/product_registration/certificates/certified/'
                ? certificateData.certified_certificate_url
                : undefined,
          });
        })
        .catch((err) => {
          console.error('Failed to fetch certificate after generation:', err);
        });

      notification.success({ message: 'Certificate Generated Successfully' });
    } catch (error) {
      console.error('Generate certificate error:', error);
      notification.error({ message: 'Error', description: 'Failed to generate certificate' });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadCertificate = async (file: File, isReupload = false) => {
    if (!id) return;
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await uploadFinalCertificate(Number(id), formData);
      console.log('Upload Certificate Response:', response);

      await getCertificate(Number(id))
        .then((data) => {
          const certificateData = data?.pr_certificates?.[0];
          setCertificate({
            key: id,
            pr_code: pr_code || '',
            certified_certificate: certificateData?.certified_certificate || null,
            generatedCertificateUrl: certificateData?.generated_certificate_url || undefined,
            completedCertificateUrl:
              certificateData?.certified_certificate_url &&
              certificateData?.certified_certificate_url !==
                'https://vraasback.ksoft.web.lk//storage/product_registration/certificates/certified/'
                ? certificateData.certified_certificate_url
                : undefined,
          });
        })
        .catch((err) => {
          console.error('Failed to fetch certificate after upload:', err);
        });

      notification.success({ message: 'Final Certificate Uploaded Successfully' });
    } catch (error) {
      console.error('Upload certificate error:', error);
      notification.error({ message: 'Error', description: 'Failed to upload final certificate' });
    } finally {
      setUploadLoading(false);
    }
  };

  const columns = [
    {
      title: 'Certificate Name',
      dataIndex: 'pr_code',
      key: 'pr_code',
      render: (text: string) => text || 'N/A',
    },
    ...(userType === 'ADMIN'
      ? [
          {
            title: 'Generated Certificate',
            key: 'generatedCertificate',
            render: (record: Certificate) => {
              if (!record.generatedCertificateUrl) {
                return (
                  <Button size="small" type="default" loading={loading} onClick={handleGenerateCertificate}>
                    Generate Certificate
                  </Button>
                );
              }

              return (
                <Button size="small" type="ghost" onClick={() => showCertificate(record.generatedCertificateUrl!)}>
                  View Generated Certificate
                </Button>
              );
            },
          },
        ]
      : []),
    ...(PRstatus === 'PENDING_REGISTRAR_APPROVAL' ||
    PRstatus === 'PENDING_CERTIFICATE_COLLECTION' ||
    PRstatus === 'PENDING_VS_APPROVAL' ||
    PRstatus === 'DONE'
      ? [
          {
            title: 'Completed Certificate',
            key: 'completedCertificate',
            render: (record: Certificate) => (
              <Row align="middle" gutter={8}>
                {userType === 'ADMIN' && record.certified_certificate === null && (
                  <BaseCol>
                    <Upload
                      showUploadList={false}
                      beforeUpload={(file) => {
                        handleUploadCertificate(file);
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />} size="small" type="default" loading={uploadLoading}>
                        Upload
                      </Button>
                    </Upload>
                  </BaseCol>
                )}

                {record.completedCertificateUrl && record.certified_certificate !== null && (
                  <BaseCol>
                    <Button size="small" type="ghost" onClick={() => showCertificate(record.completedCertificateUrl!)}>
                      View Completed Certificate
                    </Button>
                  </BaseCol>
                )}

                {userType === 'ADMIN' && record.certified_certificate !== null && PRstatus !== 'DONE' && (
                  <BaseCol>
                    <Upload
                      showUploadList={false}
                      beforeUpload={(file) => {
                        handleUploadCertificate(file, true);
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />} size="small" type="default" loading={uploadLoading}>
                        Re-upload
                      </Button>
                    </Upload>
                  </BaseCol>
                )}

                {!record.completedCertificateUrl && userType !== 'ADMIN' && (
                  <BaseCol>
                    <span>Certificate Not Uploaded Yet</span>
                  </BaseCol>
                )}
              </Row>
            ),
          },
        ]
      : []),
  ];

  return <Table dataSource={certificate ? [certificate] : []} columns={columns} pagination={false} />;
};

export default CertificateList;
